import React, { createContext, useEffect, useState, useRef, useCallback } from "react";
import Matrix, { MATRIX_LIST_SYNC_LIMIT } from "./shared/components/matrix/index.js";
import { captureChatException } from "shared/sentry";
import { isAbortError } from "./shared/utils/isAbortError.js";
import { isUserLoggedIn, getUserRole } from "./shared/utils/function";
import { AUTH_TOKEN_KEYS, ROLE } from "./shared/constants/common";
import { isMatrixSyncStale } from "./shared/components/matrix/matrixTabResume";
import { isMatrixAccessTokenStale } from "./shared/components/matrix/matrixTokenFreshness";

// Constants
const LOGIN_CHECK_INTERVAL = 13 * 60 * 1000; // 13 minutes
const SYNC_HEALTH_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes
const MAX_CATCHUP_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const MatrixContext = createContext({
  isInitialized: false,
  isTimelineReady: false,
  isUserLoggedIn: false,
  matrixUserInfo: null,
  client: null,
  serverUrl: null,
  unreadTotal: 0,
  reinitialize: () => {},
});

const MatrixProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [matrixUserInfo, setMatrixUserInfo] = useState(null);
  const [client, setClient] = useState(null);
  const [isUserLoggedInState, setIsUserLoggedInState] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [isTimelineReady, setIsTimelineReady] = useState(() =>
    typeof Matrix.getTimelineReady === "function" ? Matrix.getTimelineReady() : false,
  );
  const [serverUrl, setServerUrl] = useState(Matrix.getServerUrl?.() ?? null);
  const prevLoginStateRef = useRef(false);
  const catchupSinceRef = useRef(null);
  const initPromiseRef = useRef(null);
  const initSeqRef = useRef(0);

  /**
   * Initialize Matrix service and check user login status
   * Fetches user info and initializes Matrix client if user is logged in
   */
  const initializeMatrix = useCallback(async ({ forceReinitialize = false, loggedIn } = {}) => {
    // Collapse concurrent calls (mount + polling + storage + manual reinit).
    // Also guard against stale async completions overwriting newer state.
    const isLoggedIn = typeof loggedIn === 'boolean' ? loggedIn : isUserLoggedIn();
    setIsUserLoggedInState(isLoggedIn);

    const userRole = getUserRole();
    // Only block when role is explicitly ADMIN. Don't block when role is null/undefined
    // (e.g. right after login, before role is written to storage).
    const isRoleBlocked = userRole != null && Number(userRole) === ROLE.ADMIN;

    if (!isLoggedIn || isRoleBlocked) {
      // Invalidate any in-flight init work.
      initSeqRef.current += 1;
      // Hard reset on logout (or explicit ADMIN role) to avoid phantom listeners / background sync.
      Matrix.reset();
      setIsInitialized(false);
      setMatrixUserInfo(null);
      setClient(null);
      setServerUrl(null);
      return false;
    }

    if (!forceReinitialize && initPromiseRef.current) {
      return await initPromiseRef.current;
    }

    const seq = ++initSeqRef.current;
    const promise = (async () => {
      try {
        const credentials = await Matrix.getUserInfo();
        if (credentials?.success === false) {
          const detail = credentials.error != null ? String(credentials.error) : '';
          throw new Error(
            `Matrix credentials unavailable (${credentials.reason})${detail ? `: ${detail}` : ''}`,
          );
        }

        if (seq !== initSeqRef.current) return false;
        const user = {
          userId: credentials.userId,
          matrix_device_id: credentials.matrix_device_id,
          matrix_user_id: credentials.matrix_user_id,
          matrix_access_token: credentials.matrix_access_token,
        };
        setMatrixUserInfo(user);

        const initialized = forceReinitialize
          ? await Matrix.reinitialize(user)
          : await Matrix.initialize(user);
        if (seq !== initSeqRef.current) return false;
        setIsInitialized(initialized);

        setClient(initialized ? Matrix.getClient() : null);
        setServerUrl(Matrix.getServerUrl?.() ?? null);
        return initialized;
      } catch (error) {
        if (!isAbortError(error)) {
          captureChatException(error, { tags: { matrix: 'provider-init' } });
        }
        if (seq !== initSeqRef.current) return false;
        setIsInitialized(false);
        setMatrixUserInfo(null);
        setClient(null);
        setServerUrl(null);
        return false;
      }
    })();

    initPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      // Only clear if we're still the active init promise.
      if (initPromiseRef.current === promise) initPromiseRef.current = null;
    }
  }, []);

  /**
   * Handle login status changes and reinitialize Matrix if needed
   */
  const handleLoginStatusChange = useCallback((loggedIn = isUserLoggedIn()) => {
    const currentLoggedIn = loggedIn;
    const prevLoggedIn = prevLoginStateRef.current;

    if (currentLoggedIn !== prevLoggedIn) {
      prevLoginStateRef.current = currentLoggedIn;
      setIsUserLoggedInState(currentLoggedIn);
      initializeMatrix({ loggedIn: currentLoggedIn });
    }
  }, [initializeMatrix]);

  /**
   * Reinitialize Matrix service
   */
  const reinitialize = useCallback(async () => {
    try {
      // Keep a single guarded init pipeline for all entrypoints (manual + effects + recovery).
      return await initializeMatrix({ forceReinitialize: true });
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, { tags: { matrix: 'reinitialize' } });
      }
      setIsInitialized(false);
      setMatrixUserInfo(null);
      setClient(null);
      setServerUrl(null);
      return false;
    }
  }, [initializeMatrix]);

  // Initialize on mount and set up login status monitoring.
  // Delay Matrix boot until page is visible + 300ms so Safari (e.g. opened from Instagram)
  // has time to stabilize storage/IndexedDB/WebSocket and avoid black screen on first paint.
  useEffect(() => {
    const initialLoginState = isUserLoggedIn();
    prevLoginStateRef.current = initialLoginState;

    const start = () => {
      const state = isUserLoggedIn();
      prevLoginStateRef.current = state;
      initializeMatrix({ loggedIn: state });
    };

    let timer = null;
    let visibilityHandler = null;

    if (typeof document !== 'undefined') {
      if (document.visibilityState === 'visible') {
        timer = setTimeout(start, 300);
      } else {
        visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            timer = setTimeout(start, 300);
          }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
      }
    } else {
      timer = setTimeout(start, 300);
    }

    // Set up interval to check for login status changes
    const interval = setInterval(() => {
      handleLoginStatusChange();
    }, LOGIN_CHECK_INTERVAL);

    // Handle cross-tab login/logout via storage events
    const handleStorageChange = (e) => {
      if (e.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN) {
        handleLoginStatusChange();
      }
    };

    // Handle same-tab login/logout via custom localStorageChange event
    const handleLocalStorageChange = (e) => {
      if (e.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN) {
        handleLoginStatusChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleLocalStorageChange);

    return () => {
      if (timer) clearTimeout(timer);
      if (visibilityHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleLocalStorageChange);
    };
  }, [initializeMatrix, handleLoginStatusChange]);

  // Tab visibility → sync recovery is handled on the Matrix singleton (`tab-visible`).

  // Periodic sync health check (covers cases where sync silently stalls without emitting ERROR).
  useEffect(() => {
    const interval = setInterval(async () => {
      const matrixClient = Matrix.getClient?.();
      if (!matrixClient) {
        // Matrix service may have reset itself (e.g. token failure); ensure Provider state matches.
        if (isInitialized) {
          setIsInitialized(false);
          setMatrixUserInfo(null);
          setClient(null);
          setServerUrl(null);
        }
        return;
      }
      if (!isInitialized) return;
      if (!isUserLoggedInState) return;
      // Avoid fighting token refresh / auth recovery.
      if (Matrix.isRefreshingToken?.()) return;

      try {
        const state = matrixClient.getSyncState?.() || null;

        // Track "stuck in CATCHUP" for too long.
        if (state === 'CATCHUP') {
          if (!catchupSinceRef.current) catchupSinceRef.current = Date.now();
        } else {
          catchupSinceRef.current = null;
        }

        const catchupTooLong =
          state === 'CATCHUP' &&
          catchupSinceRef.current &&
          Date.now() - catchupSinceRef.current > MAX_CATCHUP_DURATION_MS;
        const syncStale = isMatrixSyncStale();
        const needsFreshInitialSync = catchupTooLong || syncStale;
        const syncHealthy = Matrix.isHealthySyncState?.(state);
        const tokenStale = isMatrixAccessTokenStale(Date.now(), null, {
          // Live /sync already authenticated this token. Do not mint every 2 min
          // just because issued-at was never persisted (upgrade), and do not
          // invent issued-at = now (that hides a token about to expire).
          treatUnknownAgeAsStale: !syncHealthy,
        });

        // Access token (HTTP auth) vs `since` (CS API /sync cursor) are independent.
        // Token-only rotate while SYNCING: setAccessToken, keep rooms + VoIP (no stopClient).
        // ERROR / long idle: abort the dead long-poll; drop `since` only when CATCHUP would be huge.
        // Active calls: restartSync defers until hangup (same client, callEventHandler stays).
        if (tokenStale) {
          await Matrix.refreshTokenAndUpdateClient?.({
            restartClient: needsFreshInitialSync || !syncHealthy,
            freshInitialSync: needsFreshInitialSync,
          });
        } else if (needsFreshInitialSync) {
          await Matrix.requestSyncRecovery?.({
            reason: syncStale ? 'healthcheck:sync-stale' : 'healthcheck:catchup-stuck',
            initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
            force: true,
            minIntervalMs: 0,
            freshInitialSync: true,
          });
        } else if (!state) {
          await Matrix.requestSyncRecovery?.({
            reason: 'healthcheck:unknown',
            initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
          });
        } else if (!Matrix.isHealthySyncState?.(state)) {
          await Matrix.requestSyncRecovery?.({
            reason: `healthcheck:${state}`,
            initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
          });
        }
      } catch (e) {
        if (!isAbortError(e)) {
          captureChatException(e, { tags: { matrix: 'health-check' } });
        }
        await reinitialize();
      }
    }, SYNC_HEALTH_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [isInitialized, isUserLoggedInState, reinitialize]);

  // Ensure badge clears immediately on logout (even if Matrix client resets first).
  useEffect(() => {
    if (!isUserLoggedInState) setUnreadTotal(0);
  }, [isUserLoggedInState]);

  /**
   * Keep a live "total unread messages" count for UI badges (footer, etc.).
   * The actual compute + Matrix listeners live in the Matrix service.
   */
  useEffect(() => {
    if (!isInitialized) {
      setUnreadTotal(0);
      return;
    }

    // Subscribe to the singleton service's unread tracking.
    let unsubscribe = null;
    if (typeof Matrix.subscribeUnreadTotal === 'function') {
      unsubscribe = Matrix.subscribeUnreadTotal((total) => {
        setUnreadTotal(total);
      });
    } else {
      setUnreadTotal(Matrix.getUnreadTotal?.() ?? 0);
    }

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isInitialized]);

  useEffect(() => {
    if (typeof Matrix.subscribeTimelineReady !== "function") return undefined;
    return Matrix.subscribeTimelineReady(setIsTimelineReady);
  }, []);

  const contextValue = {
    isInitialized,
    isTimelineReady,
    isUserLoggedIn: isUserLoggedInState,
    matrixUserInfo,
    client,
    serverUrl,
    unreadTotal,
    reinitialize,
  };

  return (
    <MatrixContext.Provider value={contextValue}>
      {children}
    </MatrixContext.Provider>
  );
};

export default MatrixProvider;
